import { mapValues } from 'lodash';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useAnonymousAnalyticsTrackPage } from '../../common/analytics';
import { privacyPolicyURL, termsOfUseURL } from '../../common/urls';
import {
  isSupportedLocale,
  t,
  useLocale,
} from '../../localization/LocaleContext';
import {
  useResendSetPasswordEmail,
  useSetPassword,
  useSetPasswordData,
} from '../../remoteData/unauthenticated';
import LoadingScreen from '../LoadingScreen';
import Checkbox from '../shared/Checkbox';
import PasswordField from './PasswordField';
import {
  Bullets,
  ButtonGroup,
  CheckboxContainer,
  CheckboxLabel,
  Container,
  Email,
  ErrorBlock,
  InputContainer,
  Inputs,
  SubmitButton,
  Subtitle,
  TermsText,
  Title,
} from './styledComponents';

export default function SetPassword() {
  const intl = useIntl();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const locale = searchParams.get('locale');
  const token = searchParams.get('token') ?? undefined;
  const { changeLocale, selectedLocale } = useLocale();
  useAnonymousAnalyticsTrackPage({
    name: pathname.includes('account-instructions')
      ? 'Account Instructions'
      : 'Verify Account and Set Password',
    category: 'Public',
    properties: {
      locale,
    },
  });

  const { data: setPasswordData, error: setPasswordDataError } =
    useSetPasswordData(token);
  const {
    mutate: resendSetPasswordEmail,
    error: resendSetPasswordEmailError,
    isSuccess: resendSetPasswordSuccess,
  } = useResendSetPasswordEmail();
  const {
    mutate: setPassword,
    error: setPasswordError,
    isSuccess: setPasswordSuccess,
  } = useSetPassword();

  const [showPasswords, setShowPasswords] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [marketingChecked, setMarketingChecked] = useState(false);
  const [submitPressed, setSubmitPressed] = useState(false);
  const passwordsRef = useRef<HTMLDivElement>(null);

  function validatePassword(password: string) {
    if (!password) {
      return intl.formatMessage({ id: 'general_error_password_empty' });
    }

    if (password.length < (setPasswordData?.passwordPolicy.length ?? 6)) {
      return intl.formatMessage({ id: 'general_error_password_too_short' });
    }

    return undefined;
  }

  const [passwords, setPasswords] = useState({
    password1: '',
    password2: '',
  });
  const [errors, setErrors] = useState<{
    [k in keyof typeof passwords]?: string;
  }>({});

  function validatePasswords(passes: typeof passwords) {
    const newErrors = mapValues(passes, validatePassword);

    if (!newErrors.password2 && passes.password1 !== passes.password2) {
      return {
        ...newErrors,
        password2: intl.formatMessage({
          id: 'general_error_password_no_match',
        }),
      };
    }

    return newErrors;
  }

  useLayoutEffect(() => {
    if (locale && !Array.isArray(locale) && isSupportedLocale(locale)) {
      changeLocale(locale);
    }
  }, [changeLocale, locale]);

  if (setPasswordSuccess || setPasswordData?.tokenUsed) {
    return (
      <Container>
        <Title>{t('account_setup_password_success_title')}</Title>
        <Subtitle>{t('account_setup_password_success_text')}</Subtitle>
      </Container>
    );
  }

  if (!token || setPasswordDataError) {
    return (
      <Container>
        <Title>{t('account_setup_invalid_link_title')}</Title>
        <Subtitle>{t('account_setup_invalid_link_text')}</Subtitle>
        {setPasswordDataError && (
          <ErrorBlock>{setPasswordDataError.message}</ErrorBlock>
        )}
      </Container>
    );
  }

  if (!setPasswordData) {
    return <LoadingScreen />;
  }

  if (setPasswordData.tokenExpired) {
    return (
      <Container>
        <Title>{t('account_setup_link_expired_title')}</Title>
        <Subtitle>{t('account_setup_link_expired_text')}</Subtitle>
        {resendSetPasswordSuccess ? (
          <Subtitle style={{ marginTop: '1.5rem' }}>
            {t('account_setup_resend_email_success')}
          </Subtitle>
        ) : resendSetPasswordEmailError ? (
          <ErrorBlock>{resendSetPasswordEmailError.message}</ErrorBlock>
        ) : (
          <SubmitButton
            size="xsmall"
            rounded
            onClick={() => {
              resendSetPasswordEmail({ token, locale: selectedLocale });
            }}
          >
            {t('account_setup_resend_email_button')}
          </SubmitButton>
        )}
      </Container>
    );
  }

  return (
    <Container>
      <Title>{t('account_setup_title')}</Title>
      <Subtitle>
        {t('account_setup_text', {
          email: <Email>{setPasswordData.email}</Email>,
        })}
      </Subtitle>
      <Inputs ref={passwordsRef}>
        {(['password1', 'password2'] as const).map((field) => (
          <InputContainer key={field}>
            <PasswordField
              key={field}
              name={field}
              value={passwords[field]}
              error={errors[field]}
              inputProps={{
                'aria-invalid': !!errors[field],
                autoComplete: 'new-password',
              }}
              data-testid={`input-${field}`}
              type={showPasswords ? 'text' : 'password'}
              label={intl.formatMessage({
                id:
                  field === 'password1'
                    ? 'general_password_fieldtext'
                    : 'general_confirm_password_fieldtext',
              })}
              onChange={(event) => {
                const updatedPasswords = {
                  ...passwords,
                  [field]: event.target.value,
                };
                setPasswords(updatedPasswords);

                if (submitPressed) {
                  // After we've submitted once, validate when the user types
                  setErrors(validatePasswords(updatedPasswords));
                }
              }}
              showPassword={showPasswords}
              toggleShowPassword={() => setShowPasswords((state) => !state)}
            />
          </InputContainer>
        ))}
      </Inputs>
      <TermsText>
        {t('account_setup_updated_terms_text', {
          pp: (chunks: ReactNode) => (
            <a href={privacyPolicyURL(selectedLocale)}>{chunks}</a>
          ),
          tos: (chunks: ReactNode) => (
            <a href={termsOfUseURL(selectedLocale)}>{chunks}</a>
          ),
        })}
      </TermsText>
      <Bullets>
        <li>
          <span>{t('account_setup_bullet1')}</span>
        </li>
        <li>
          <span>{t('account_setup_bullet2')}</span>
        </li>
        <li>
          <span>{t('account_setup_bullet3')}</span>
        </li>
        <li>
          <span>{t('account_setup_bullet4')}</span>
        </li>
      </Bullets>
      <CheckboxLabel>
        <CheckboxContainer>
          <Checkbox
            checked={marketingChecked}
            data-testid="marketing-checkbox"
            iconStyle={{ position: 'relative', top: -2 }}
            onChange={() => {
              setMarketingChecked((checked) => !checked);
            }}
          />
        </CheckboxContainer>
        <div>{t('account_setup_marketing_consent_checkbox')}</div>
      </CheckboxLabel>
      <CheckboxLabel>
        <CheckboxContainer>
          <Checkbox
            checked={termsChecked}
            data-testid="terms-checkbox"
            iconStyle={{ position: 'relative', top: -2 }}
            onChange={() => {
              setTermsChecked((checked) => !checked);
            }}
          />
        </CheckboxContainer>
        <div>{t('account_setup_accept_health_data_checkbox')}</div>
      </CheckboxLabel>
      <ButtonGroup>
        <SubmitButton
          size="xsmall"
          rounded
          type="submit"
          onClick={() => {
            const errs = validatePasswords(passwords);
            setErrors(errs);
            setSubmitPressed(true);
            if (Object.values(errs).every((e) => !e)) {
              // No errors
              if (termsChecked) {
                setPassword({
                  password: passwords.password1,
                  passwordConfirmation: passwords.password2,
                  token,
                  privacyPolicyVersion: setPasswordData.privacyPolicyVersion,
                  termsOfServiceVersion: setPasswordData.termsOfServiceVersion,
                  acceptTOS: termsChecked,
                  acceptMarketing: marketingChecked,
                });
              }
            } else {
              passwordsRef.current?.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          disabled={!termsChecked}
        >
          {t('account_setup_verify_and_save_button')}
        </SubmitButton>
        {setPasswordError && (
          <ErrorBlock style={{ marginTop: '1rem' }}>
            {/* FIXME: This is not localized. */}
            {setPasswordError.responseContent?.detail ||
              setPasswordError.responseContent?.title ||
              setPasswordError.message}
          </ErrorBlock>
        )}
      </ButtonGroup>
    </Container>
  );
}
